import React, { useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import UploadScreen from './components/UploadScreen';
import BulkFormHelper from './components/BulkFormHelper';
import ProductTypeSelectionStep from './components/ProductTypeSelectionStep';
import SelectProductCatalogueModal from '../../components/UI/SelectProductCatalogueModal';
import AuthContext from '../../context/AuthContext';

const BulkListingGenerator = () => {
    const location = useLocation();
    const authCtx = useContext(AuthContext);
    const isNewMode = location.state?.isNewListing === true;
    const projectName = location.state?.projectName || null;

    const [showProductTypeStep, setShowProductTypeStep] = useState(isNewMode);
    const [showFormHelper, setShowFormHelper] = useState(false);

    // Sync showProductTypeStep when isNewMode changes (e.g. route state arrives after mount)
    useEffect(() => {
        if (isNewMode) setShowProductTypeStep(true);
    }, [isNewMode]);
    const [parsedData, setParsedData] = useState(null);
    const [zipFile, setZipFile] = useState(null);
    const [draftMeta, setDraftMeta] = useState(null); // { draftId, draftData }
    // Product Catalogue Modal State
    const [showProductModal, setShowProductModal] = useState(false);
    const [products, setProducts] = useState([]);
    const [selectedAsins, setSelectedAsins] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [asinsToAdd, setAsinsToAdd] = useState([]);

    const handleShowFormHelper = (data, zipFileObj) => {
        setParsedData(data);
        setZipFile(zipFileObj);
        setShowFormHelper(true);
    };

    // ── Load bulk draft from route state ────────────────────────────────
    useEffect(() => {
        if (!location.state?.loadBulkDraft || !location.state?.draftData) return;

        const draft = location.state.draftData;
        const draftData = draft.draft_data;
        if (!draftData) return;

        const loadDraft = async () => {
            let prefetchedSkuImages = {};

            // Download and extract images from S3 ZIP if available
            if (draft.images_zip_url) {
                try {
                    const JSZip = (await import('jszip')).default;
                    const response = await fetch(draft.images_zip_url);
                    const zipBlob = await response.blob();
                    const zip = await new JSZip().loadAsync(zipBlob);
                    const filePromises = [];

                    Object.keys(zip.files).forEach((filename) => {
                        const file = zip.files[filename];
                        if (file.dir || filename.startsWith('__MACOSX') || filename.includes('.DS_Store')) return;
                        const lowerName = filename.toLowerCase();
                        if (!lowerName.match(/\.(png|jpg|jpeg|webp)$/)) return;

                        const parts = filename.split('/').filter(p => p && !p.startsWith('.'));
                        if (parts.length < 2) return;
                        const skuFolder = parts[0];
                        const imageName = parts[parts.length - 1];

                        filePromises.push(
                            file.async('blob').then(blob => {
                                const imageFile = new File([blob], imageName, { type: blob.type || 'image/png' });
                                return new Promise((resolve) => {
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                        if (!prefetchedSkuImages[skuFolder]) prefetchedSkuImages[skuFolder] = [];
                                        prefetchedSkuImages[skuFolder].push({ file: imageFile, previewUrl: reader.result, name: imageName });
                                        resolve();
                                    };
                                    reader.readAsDataURL(blob);
                                });
                            })
                        );
                    });
                    await Promise.all(filePromises);
                } catch (err) {
                    console.error('Failed to load draft images:', err);
                }
            }

            // Reconstruct parsedData for the form
            // Rebuild skuFormSnapshots with images matched by name
            const restoredSnapshots = {};
            if (draftData.skuFormSnapshots) {
                for (const [sku, snap] of Object.entries(draftData.skuFormSnapshots)) {
                    const skuImgs = prefetchedSkuImages[sku] || [];
                    const matchImage = (name) => skuImgs.find(img => img.name === name) || null;

                    restoredSnapshots[sku] = {
                        formData: snap.formData,
                        existingInput: snap.existingInput,
                        dimensionData: snap.dimensionData ? {
                            ...snap.dimensionData,
                            dimensionImage: snap.dimensionImageName ? matchImage(snap.dimensionImageName) : null,
                        } : null,
                        bPlusSequence: (snap.bPlusImageNames || []).map(matchImage).filter(Boolean),
                        aPlusSequence: (snap.aPlusImageNames || []).map(matchImage).filter(Boolean),
                        zoomAreas: snap.zoomAreas || [],
                    };
                }
            }

            // Build allExcelRows from skuList + snapshots (so the form can reconstruct)
            const allExcelRows = (draftData.skuList || []).map((skuItem) => {
                const snap = restoredSnapshots[skuItem.sku];
                const fd = snap?.formData || {};
                const ei = snap?.existingInput || {};
                return {
                    'SKU': skuItem.sku,
                    'Brand': fd.brandName || '',
                    'Category': fd.category || '',
                    'Sub Category': fd.subCategory || '',
                    'Product Type': fd.productType || '',
                    'Title': skuItem.title || ei.title || '',
                    'Color': fd.color || '',
                    'Material': fd.material || '',
                    'Size': fd.size || '',
                    'EAN': fd.eanNumber || '',
                    'Keywords': fd.keywords || '',
                    'Description': fd.briefDescription || ei.description || '',
                    'Core Product Details 1': fd.feature1 || '',
                    'Core Product Details 2': fd.feature2 || '',
                    'Core Product Details 3': fd.feature3 || '',
                    'Core Product Details 4': fd.feature4 || '',
                    'Core Product Details 5': fd.feature5 || '',
                    'Bullet Point 1': ei.bullets?.[0] || '',
                    'Bullet Point 2': ei.bullets?.[1] || '',
                    'Bullet Point 3': ei.bullets?.[2] || '',
                    'Bullet Point 4': ei.bullets?.[3] || '',
                    'Bullet Point 5': ei.bullets?.[4] || '',
                    'is_parent': skuItem.isParent ? 'true' : 'false',
                    'Parent SKU': skuItem.parentSku || '',
                    // New mode extra fields
                    'Banned Keywords': fd.bannedKeywords || '',
                    'Application 1': fd.application1 || '',
                    'Application 2': fd.application2 || '',
                    // Parent-child attributes
                    ...(skuItem.parentChildAttributes?.[0] ? {
                        'Parent Child Attribute 1': skuItem.parentChildAttributes[0].name,
                        'Parent Child Attribute 1 Value': skuItem.parentChildAttributes[0].value,
                    } : {}),
                    ...(skuItem.parentChildAttributes?.[1] ? {
                        'Parent Child Attribute 2': skuItem.parentChildAttributes[1].name,
                        'Parent Child Attribute 2 Value': skuItem.parentChildAttributes[1].value,
                    } : {}),
                    ...(skuItem.parentChildAttributes?.[2] ? {
                        'Parent Child Attribute 3': skuItem.parentChildAttributes[2].name,
                        'Parent Child Attribute 3 Value': skuItem.parentChildAttributes[2].value,
                    } : {}),
                    // Dimension
                    ...(snap?.dimensionData ? {
                        'Length': snap.dimensionData.length || '',
                        'Breadth': snap.dimensionData.breadth || '',
                        'Height': snap.dimensionData.height || '',
                        'Units': snap.dimensionData.units || '',
                    } : {}),
                };
            });

            const reconstructedParsedData = {
                allExcelRows,
                generationType: draftData.generationType || 'complete_listing',
                prefetchedSkuImages,
                // Pass restored snapshots so useBulkFormState can use them
                draftSnapshots: restoredSnapshots,
                draftThemesBySku: draftData.themesBySku || {},
                files: [],
            };

            setDraftMeta({ draftId: draft.draft_id, draftData: draftData });
            setParsedData(reconstructedParsedData);
            setShowFormHelper(true);
            setShowProductTypeStep(false);

            // Clear location state to prevent re-loading on navigation
            window.history.replaceState({}, document.title);
        };

        loadDraft();
    }, [location.state]);


    const handleBackToUpload = () => {
        setShowFormHelper(false);
    };

    const handleContinueFromProductTypes = () => {
        setShowProductTypeStep(false);
    };

    const fetchProducts = async () => {
        setLoadingProducts(true);
        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_URL}/amazon/catalogue`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                setProducts(data.products || []);
                setShowProductModal(true);
            }
        } catch (err) {
            console.error('Error fetching catalogue:', err);
        } finally {
            setLoadingProducts(false);
        }
    };

    const handleOpenProductModal = () => {
        fetchProducts();
    };

    const handleCloseProductModal = () => {
        setShowProductModal(false);
        setSelectedAsins([]);
    };

    const handleAsinSelect = (asinValue) => {
        setSelectedAsins(prev =>
            prev.includes(asinValue)
                ? prev.filter(a => a !== asinValue)
                : [...prev, asinValue]
        );
    };

    const handleSelectAll = () => {
        const newSelectAll = !isAllSelected;
        setSelectedAsins(newSelectAll ? products.map(p => p.asin) : []);
    };

    // Derive selectAll from selectedAsins (no extra state sync needed)
    const isAllSelected = products.length > 0 && selectedAsins.length === products.length;

    const handleModalAnalyze = () => {
        if (selectedAsins.length > 0) {
            setShowProductModal(false);
            setAsinsToAdd(selectedAsins);
            setSelectedAsins([]);
        }
    };

    if (showFormHelper) {
        return (
            <BulkFormHelper
                parsedData={parsedData}
                zipFile={zipFile}
                onBack={handleBackToUpload}
                isNewMode={isNewMode}
                draftMeta={draftMeta}
                projectName={projectName}
            />
        );
    }

    if (isNewMode && showProductTypeStep) {
        return (
            <ProductTypeSelectionStep onContinue={handleContinueFromProductTypes} />
        );
    }

    return (
        <>
            <UploadScreen
                listingMode={isNewMode ? 'new' : 'existing'}
                onShowFormHelper={handleShowFormHelper}
                onFetchFromCatalogue={handleOpenProductModal}
                loadingProducts={loadingProducts}
                catalogueProducts={products}
                asinsToAdd={asinsToAdd}
                onAsinsAdded={() => setAsinsToAdd([])}
            />
            <SelectProductCatalogueModal
                show={showProductModal}
                onClose={handleCloseProductModal}
                products={products}
                selectedAsins={selectedAsins}
                onAsinSelect={handleAsinSelect}
                actionLabel="Select"
                onSelectAll={handleSelectAll}
                onAnalyze={handleModalAnalyze}
                selectAll={isAllSelected}
            />
        </>
    );
};

export default BulkListingGenerator;
